import React, { useState } from 'react';
import { Mail, User, Clock, MessageSquare, Send, RefreshCw, X } from 'react-feather';
import { httpsCallable } from 'firebase/functions';
import { updateDoc, doc } from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import './styles/MessageBox.css';

const MessageBox = ({ message, onReplySuccess }) => {
  const [replyText, setReplyText] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim()) {
      setError('Please enter a reply message');
      return;
    }
    
    setSending(true);
    setError('');
    
    try {
      console.log("Starting reply process for message ID:", message.id);
      
      // First update Firestore directly - this should trigger the onUpdate cloud function
      try {
        console.log("Updating Firestore document");
        await updateDoc(doc(db, 'messages', message.id), {
          reply: replyText,
          status: 'replied',
          repliedAt: new Date().toISOString()
        });
        console.log("Firestore document updated successfully");
      } catch (firestoreError) {
        console.error("Error updating Firestore:", firestoreError);
        throw new Error(`Firestore update failed: ${firestoreError.message}`);
      }
      
      // Also try to call the function directly as backup
      try {
        console.log("Calling Cloud Function directly");
        const sendMessageReply = httpsCallable(functions, 'sendMessageReply');
        
        const result = await sendMessageReply({
          messageId: message.id,
          replyText: replyText,
          // Add more context to help diagnose issues
          email: message.email,
          name: message.firstName,
          subject: message.subject || 'Your Message'
        });
        
        console.log("Cloud Function response:", result);
      } catch (functionError) {
        // Log but don't fail the overall operation if direct function call fails
        console.error("Error calling function directly:", functionError);
        console.error("Function error details:", {
          code: functionError.code,
          message: functionError.message,
          details: functionError.details
        });
        
        // The update already succeeded, so we'll continue but note the error
        console.log("Continuing despite function error since Firestore was updated");
      }
      
      // Reset form and show success
      setReplyText('');
      setShowReplyForm(false);
      
      // Notify parent component of success
      if (onReplySuccess) {
        onReplySuccess(message.id);
      }
    } catch (error) {
      console.error('Detailed error:', error);
      // Provide more specific error message to user
      setError(`Error: ${error.code || error.message || 'Unknown error occurred'}`);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // If it's a Firestore timestamp
    if (dateString && dateString.seconds) {
      const date = new Date(dateString.seconds * 1000);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    // If it's an ISO string
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className={`message-box ${message.status === 'replied' ? 'status-replied' : 'status-unread'}`}>
      <div className="message-header">
        <div className="message-sender">
          <User size={16} className="icon" />
          <span className="sender-name">{message.firstName} {message.lastName}</span>
        </div>
        <span className={`status-badge ${message.status || 'unread'}`}>
          {message.status || 'unread'}
        </span>
      </div>
      
      <div className="message-contact">
        <Mail size={16} className="icon" />
        <a href={`mailto:${message.email}`} className="email-link">
          {message.email}
        </a>
      </div>
      
      <div className="message-timestamp">
        <Clock size={16} className="icon" />
        <span>Received: {formatDate(message.createdAt)}</span>
      </div>
      
      {message.subject && (
        <div className="message-subject">
          <strong>Subject:</strong> {message.subject}
        </div>
      )}
      
      <div className="message-content">
        <p>{message.message}</p>
        {message.additionalDetails && (
          <div className="additional-details">
            <p><strong>Additional Details:</strong></p>
            <p>{message.additionalDetails}</p>
          </div>
        )}
      </div>
      
      {message.status === 'replied' ? (
        <div className="reply-content">
          <div className="reply-header">
            <MessageSquare size={16} className="icon" />
            <span>Your Reply ({formatDate(message.repliedAt)})</span>
          </div>
          <p>{message.reply}</p>
        </div>
      ) : (
        <div className="reply-section">
          {!showReplyForm ? (
            <button 
              className="reply-button"
              onClick={() => setShowReplyForm(true)}
            >
              <Send size={16} /> Reply via Email
            </button>
          ) : (
            <form onSubmit={handleReplySubmit} className="reply-form">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                rows="4"
                required
              />
              
              {error && <div className="reply-error">{error}</div>}
              
              <div className="form-actions">
                <button type="submit" className="send-button" disabled={sending}>
                  {sending ? <RefreshCw size={16} className="spinning" /> : <Send size={16} />} 
                  {sending ? 'Sending...' : 'Send Reply'}
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setShowReplyForm(false);
                    setError('');
                  }}
                >
                  <X size={16} /> Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageBox;